import PropTypes from 'prop-types';
import React from 'react';
import { showToast } from 'stores/ToastStore';
import SessionStore from 'stores/SessionStore';
import AccountingWebAPIUtils, {
  setPayoutPaymentMethod,
} from 'utils/AccountingWebAPIUtils';
import {
  getShipmentByOid,
  getShipmentsLedgers,
} from 'utils/ShipmentWebAPIUtils';
import { getOrder } from 'utils/OrderWebAPIUtils';
import { isLegManagementEnabled } from 'helpers/legs-helper';
import AccountingActionCreators from 'actions/AccountingActionCreators';
import CustomersAPI from 'utils/CustomersAPIUtils';
import PayoutStore from 'stores/accounting/PayoutStore';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import PayoutJobList from './v2/PayoutJobList';
import PayoutHeader from './v2/PayoutHeader';
import ShipmentSearch from 'components/site/shipment-search';
import { withTranslation } from 'react-i18next';
import {
  BadgeAlt as Badge,
  Button,
  Card,
  CardHeader,
  CardContent,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@cargomatic/ui';
import {
  CheckCircle,
  DollarSign,
  Download,
  ExternalLink,
  Mail,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import moment from 'moment';
import { titleize } from 'underscore.string';
import { unescapeSpecialChars } from 'helpers/helper_functions';
import { AuthorizationWrapper } from 'components/wrappers/AuthorizationWrapper';
import { REGIONS } from 'constants/Regions';
import { sluggify } from '../../../../utils/localization/utils';
import PaymentSummary from '../payment-summary';
import PaymentHistory from '../payment-history';
import LogPaymentModal from '../log-payment-modal';
import PaymentMethodDialog from '../PaymentMethodDialog';
import SendEmailModal from '../send-email-modal';
import { fetchSignedUrlByForeignKey } from '../../../../utils/DocumentsAPIUtils';

import {
  paymentMethods,
  paymentStatus,
} from '../../../../constants/accounting';
import HistoryTable from '../history-table';

const setPayoutIdOnPayoutLineItems = (payoutLineItemGroups, payoutId) => {
  return (payoutLineItemGroups ?? []).map((payoutLineItemGroup) => ({
    ...payoutLineItemGroup,
    lineItems: (payoutLineItemGroup.lineItems ?? []).map((lineItem) => ({
      ...lineItem,
      payout_id: payoutId,
    })),
  }));
};

const getStateFromStores = (id) => {
  let payout = PayoutStore.getPayout(id);
  let balance = 0;
  if (payout) {
    balance = payout.amount_due;
    if (payout.lineItemGroups) {
      payout = {
        ...payout,
        lineItemGroups: setPayoutIdOnPayoutLineItems(
          payout.lineItemGroups,
          payout._id,
        ),
      };
    }
  }

  return {
    payout: payout,
    balance: balance,
    loading: false,
  };
};

const STRIPE = 'automated';
const ACH = 'manual';

const JOBLIST_ACTIONS = {
  ADD: 'add',
  REMOVE: 'remove',
};

class PayoutDetails extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.getInitialState();
  }

  getInitialState() {
    return {
      loading: true,
      selectedValue: '',
      paymentMethod: null,
      isAdmin: SessionStore.isAdmin(),
      editMode: false,
      updates: [],
      showLineItemConfirmModal: false,
      showPaymentMethodDialog: false,
      tempPaymentMethod: null,
      isSavingPaymentMethod: false,
      activeTab: 'jobs',
      showLogPaymentModal: false,
      confirmModal: this.confirmModalDefault(),
      isFinanceAdmin: SessionStore.isFinanceAdmin(),
      hasFinanceAdminRole: SessionStore.hasFinanceAdminRole(),
      showSendEmailModal: false,
      isReprocessing: false,
      showApprovalModal: false,
      showVoidModal: false,
    };
  }

  componentDidMount() {
    PayoutStore.register(this.onChange);
    AccountingActionCreators.getPayout(this.getPayoutId());
  }

  componentWillUnmount() {
    PayoutStore.unregister(this.onChange);
  }

  componentDidUpdate(_prevProps, prevState) {
    if (!prevState.payout && this.state.payout) {
      this.populateFundingData(this.state.payout);
    }
  }

  populateFundingData(payoutArg) {
    const carrierId = payoutArg?.carrier?._id;
    if (!carrierId) return;
    CustomersAPI.getBankAccounts(carrierId)
      .then((res) => {
        this.setState((prevState) => {
          const response = res?.entity?.data;
          return {
            payout: {
              ...prevState.payout,
              carrier: {
                ...prevState.payout.carrier,
                bank_accounts: response?.external_accounts,
              },
            },
          };
        });
      })
      .catch((err) => {
        console.error(err.stack || err);
      });
  }

  getPayoutId() {
    return this.props.payoutId || this.props.match?.params?.id;
  }

  onChange = async () => {
    const payoutState = getStateFromStores(this.getPayoutId());
    if (
      payoutState.payout &&
      payoutState.payout.jobs?.length &&
      !this.state.order
    ) {
      try {
        const shipment = await getShipmentByOid(payoutState.payout.jobs[0]);
        const orderId = shipment?.order?._id || shipment?.order;
        if (orderId) {
          const { entity } = await getOrder(orderId);
          this.setState({ ...payoutState, order: entity?.order });
          return;
        }
      } catch (e) {
        console.error('Error fetching order for payout:', e);
      }
    }
    this.setState(payoutState);
  };

  handlePayoutUpdated = (updatedPayout) => {
    this.setState({ payout: updatedPayout });
  };

  sendEmail = (emails) => {
    AccountingActionCreators.emailPayout(this.state.payout?._id, emails);
  };

  handleDownload = async () => {
    const { t } = this.props;
    const newWindow = window.open('', '_blank');
    try {
      const { url, error } = await fetchSignedUrlByForeignKey(
        this.state.payout?._id,
      );
      if (url) {
        if (newWindow) {
          newWindow.opener = null;
          newWindow.location.href = url;
        }
      } else {
        newWindow?.close();
        showToast(t('GET_SIGNED_URL_ERROR'), error, 'error');
      }
    } catch (e) {
      newWindow?.close();
      showToast(t('GET_SIGNED_URL_ERROR'), e?.message, 'error');
    }
  };

  reprocessCarrierPayout = async () => {
    const { t } = this.props;
    this.setState({ isReprocessing: true });
    try {
      const response = await AccountingWebAPIUtils.reprocessCarrierPayout(
        this.state.payout?._id,
      );
      if (response && response.payout) {
        const updatedPayout = {
          ...response.payout,
          jobs: (response.payout.jobs ?? []).map(({ _id }) => _id),
        };
        this.handlePayoutUpdated(updatedPayout);
        showToast(
          t('REPROCESS_SUCCESS'),
          t('SUCCESSFULLY_REPROCESSED_PAYOUT'),
          'success',
        );
      }
    } catch (entity) {
      const err = entity?.error;
      if (err) {
        showToast(
          t('REPROCESS_ERROR'),
          t('ERROR_REPROCESSING_WITH_VALUE', { error: err }),
          'error',
        );
      }
    }
    this.setState({ isReprocessing: false });
  };

  handleApprovalConfirm = () => {
    const { payout } = this.state;
    this.setState({ showApprovalModal: false }, () => {
      if (payout?.status === 'approved_internally') {
        AccountingActionCreators.reversePayoutApproval(payout._id);
      } else {
        AccountingActionCreators.approvePayout(payout._id);
      }
    });
  };

  handleVoidConfirm = () => {
    const { payout } = this.state;
    this.setState({ showVoidModal: false }, () => {
      if (payout?.status === 'void') {
        AccountingActionCreators.reversePayoutVoid(payout._id);
      } else {
        AccountingActionCreators.voidPayout(payout._id);
      }
    });
  };

  savePayment = (payment) => {
    AccountingActionCreators.recordPaymentForPayout(
      this.state.payout?._id,
      payment,
    );
  };

  defineDropdownSelectedValue() {
    const { paymentMethod, payout } = this.state;
    const payment_method = payout?.payment_method;
    if (paymentMethod) {
      return paymentMethod === 'automated' ? STRIPE : ACH;
    }
    if (payment_method) {
      return payment_method === 'automated' ? STRIPE : ACH;
    }
    return '';
  }

  render() {
    const { t } = this.props;
    if (this.state.loading) {
      return (
        <p data-testid="p-8d6c1304" className="px-4 py-5 sm:p-6">
          {t('LOADING')}
        </p>
      );
    }

    return this.renderPayout();
  }

  handlePaymentMethodSelect = async (value) => {
    if (!value) {
      return;
    }
    const method = value.toLowerCase();
    const currentMethod =
      this.state.paymentMethod || this.state.payout?.payment_method;
    if (method && method !== currentMethod) {
      const previousMethod = currentMethod || null;
      const method_value = method === 'manual' ? ACH : STRIPE;
      try {
        await setPayoutPaymentMethod(method_value, this.state.payout?._id);
        this.setState({ paymentMethod: method });
        showToast('Payment method updated', null, 'success');
      } catch (e) {
        console.error(e);
        this.setState({ paymentMethod: previousMethod });
        showToast('Failed to update payment method', null, 'error');
      }
    }
  };

  handleSavePaymentMethod = async () => {
    const { tempPaymentMethod } = this.state;
    if (tempPaymentMethod) {
      this.setState({ isSavingPaymentMethod: true });
      try {
        await this.handlePaymentMethodSelect(tempPaymentMethod);
        this.setState({ showPaymentMethodDialog: false });
      } finally {
        this.setState({ isSavingPaymentMethod: false });
      }
    } else {
      this.setState({ showPaymentMethodDialog: false });
    }
  };

  getPaymentMethodLabel() {
    const { t } = this.props;
    const selectedValue = this.defineDropdownSelectedValue();
    const method = paymentMethods.find((m) => m.value === selectedValue);
    return method ? t(method.localizationSlug) : t('SELECT_PAYMENT_METHOD');
  }

  canMakePayment(payout) {
    return (
      payout.status === 'approved_internally' ||
      payout.status === 'payment_initialized'
    );
  }

  setChanges = (updates) => {
    const { editMode } = this.state;
    this.setState({ updates });

    if (!editMode) {
      this.setState({ editMode: true });
    }
  };

  handleEditButton = async () => {
    const { t } = this.props;
    const { editMode, payout, updates } = this.state;

    if (!editMode) {
      return this.setState({ editMode: true });
    }
    if (!updates.changes || !updates.changes.length) {
      return this.setState({ editMode: false });
    }
    if (updates.changes && updates.changes.length) {
      try {
        const updatedPayout = await AccountingWebAPIUtils.updatePayoutLineItems(
          payout._id,
          payout.version,
          updates.changes,
        );

        this.setState({
          editMode: false,
          payout: updatedPayout,
          updates: [],
        });
      } catch (e) {
        showToast(
          t('UPDATE_LINE_ITEMS_ERROR'),
          e?.entity?.error || e?.message,
          'error',
        );
      }
    }
  };

  handleJobSelect = async (payout, job) => {
    const { t } = this.props;
    if ((payout.jobs ?? []).includes(job._id)) {
      return;
    }

    if (payout.status === 'paid' || payout.status === 'payment_initialized') {
      return;
    }

    this.setState({ showLineItemConfirmModal: false });
    try {
      const updatedJobIds = [...(payout.jobs ?? []), job._id];
      const newPayout = await AccountingWebAPIUtils.updatePayoutJobs(
        payout,
        updatedJobIds,
        JOBLIST_ACTIONS.ADD,
      );

      this.setState({ payout: newPayout });
    } catch (e) {
      showToast(t('JOB_ADD_ERROR'), e?.entity?.error || e?.message, 'error');
    }
  };

  handleJobRemove = async (payout, lineItem) => {
    const { t } = this.props;
    try {
      const updatedJobIds = (payout.jobs ?? []).filter(
        (id) => id !== lineItem.shipment_id,
      );
      this.setState({ showLineItemConfirmModal: false });
      const newPayout = await AccountingWebAPIUtils.updatePayoutJobs(
        payout,
        updatedJobIds,
        JOBLIST_ACTIONS.REMOVE,
      );

      showToast(
        t('JOB_REMOVE_SUCCESS'),
        t('SUCCESSFULLY_REMOVED_JOB'),
        'success',
      );

      this.setState({
        payout: newPayout,
        confirmModal: this.confirmModalDefault(),
      });
    } catch (e) {
      showToast(t('JOB_REMOVE_ERROR'), e?.entity?.error || e?.message, 'error');
    }
  };

  allowPaymentSelection() {
    const { hasFinanceAdminRole, payout } = this.state;
    const status = payout?.status;
    const stripe_account_id = payout?.carrier?.stripe_account_id;
    return (
      hasFinanceAdminRole &&
      stripe_account_id &&
      (status === paymentStatus.APPROVED_INTERNALLY ||
        status === paymentStatus.INTERNAL_REVIEW)
    );
  }

  handleSearch = async (payout, value, job) => {
    const originalGroups =
      this.state.originalLineItemGroups ?? payout.lineItemGroups ?? [];
    const searchedShipmentId = job.shipmentId;
    const listOfJobs = originalGroups.filter(
      (jobsAssociated) => jobsAssociated.shipmentId === searchedShipmentId,
    );
    if (listOfJobs.length) {
      this.setState({
        selectedValue: value,
        originalLineItemGroups: originalGroups,
        payout: { ...payout, lineItemGroups: listOfJobs },
      });
    } else {
      this.setState({ selectedValue: value });
    }
  };

  onSelectChange = (value) => {
    const payoutState = getStateFromStores(this.getPayoutId());
    const { selectedValue, originalLineItemGroups } = this.state;
    const lineItemGroups =
      originalLineItemGroups ?? payoutState.payout?.lineItemGroups ?? [];
    value = value.trim().toUpperCase();
    if (value.length && value !== selectedValue) {
      const filteredJobs = lineItemGroups.filter((lineItemGroup) =>
        lineItemGroup.shipmentId?.includes(value),
      );
      this.setState({
        payout: { ...payoutState.payout, lineItemGroups: filteredJobs },
      });
    } else {
      this.setState({ ...payoutState, originalLineItemGroups: null });
    }
  };

  confirmModalDefault = () => {
    return {
      title: '',
      message: '',
      onConfirm: null,
    };
  };

  confirmAddShipment = async (job) => {
    try {
      const { t } = this.props;
      const ledgersResult = await getShipmentsLedgers([job._id], 'payout');

      const jobLineItems =
        ledgersResult && ledgersResult.items && ledgersResult.items.length
          ? ledgersResult.items[0]
          : {};

      const hasOtherPaidItems = (jobLineItems.payables || []).some(
        (lineItem) => {
          if (!lineItem.payout_id) {
            return false;
          }
          return (
            lineItem.payout &&
            lineItem.payout.status !== 'paid' &&
            lineItem.payout?._id?.toString() !==
              this.state.payout?._id?.toString()
          );
        },
      );

      const hasPaidLineItemsEligibleToMove = (jobLineItems.payables ?? []).some(
        (lineItem) => {
          if (!lineItem.invoice_id) {
            return true;
          }
          return lineItem.payout && lineItem.payout.status !== 'paid';
        },
      );

      const hasUnpaidLineItems = (job.payables ?? []).some(
        (payable) => !payable.payout_id,
      );

      if (!hasPaidLineItemsEligibleToMove && !hasUnpaidLineItems) {
        const title = t('UNABLE_TO_ADD_SHIPMENT_TO_PAYOUT_TITLE', {
          shipmentId: job.shipment_id,
        });
        const message = t('UNABLE_TO_ADD_SHIPMENT_TO_PAYOUT_MESSAGE');
        this.setState({
          showLineItemConfirmModal: true,
          confirmModal: {
            title,
            message,
            onConfirm: () =>
              this.setState({
                showLineItemConfirmModal: false,
                confirmModal: this.confirmModalDefault(),
              }),
          },
        });
      } else if (hasOtherPaidItems || hasUnpaidLineItems) {
        const title = t('ADD_SHIPMENT_TO_PAYOUT_TITLE', {
          shipmentId: job.shipment_id,
        });
        const message = t('ADD_SHIPMENT_TO_PAYOUT_MESSAGE');
        this.setState({
          showLineItemConfirmModal: true,
          confirmModal: {
            title,
            message,
            onConfirm: () => this.handleJobSelect(this.state.payout, job),
          },
        });
      } else {
        this.handleJobSelect(this.state.payout, job);
      }
    } catch (error) {
      console.error(error);
    }
  };

  confirmRemoveShipment = (job) => {
    const { t } = this.props;
    const title = t('REMOVE_SHIPMENT_TO_PAYOUT_TITLE', {
      shipmentId: job.shipmentId,
    });
    const message = t('REMOVE_SHIPMENT_TO_PAYOUT_MESSAGE');
    this.setState({
      showLineItemConfirmModal: true,
      confirmModal: {
        title,
        message,
        onConfirm: () => this.handleJobRemove(this.state.payout, job),
      },
    });
  };

  renderPayoutV1() {
    const {
      payout,
      isAdmin,
      confirmModal: { title, message, onConfirm },
    } = this.state;
    const lineItemGroups = payout.lineItemGroups ?? [];
    const { t } = this.props;
    const selectedValue = this.defineDropdownSelectedValue();
    const { status } = payout;
    const PAYOUT_STATUS_COLOR = {
      approved_internally: 'green',
      payment_initialized: 'blue',
      paid: 'brand',
      void: 'red',
    };
    const payoutStatusColor = PAYOUT_STATUS_COLOR[status] || 'gray';

    const BADGE_COLOR_MAP = {
      green: 'border-emerald-200 bg-emerald-50 text-emerald-700',
      red: 'border-red-200 bg-red-50 text-red-700',
      blue: 'border-blue-200 bg-blue-50 text-blue-700',
      brand: 'border-emerald-200 bg-emerald-50 text-emerald-700',
      gray: 'border-gray-200 bg-gray-50 text-gray-700',
    };

    const showApprovalAction =
      status !== 'payment_initialized' &&
      status !== 'paid' &&
      status !== 'void';
    const approvalAction =
      status === 'approved_internally' ? 'Reverse Approval' : 'Approve';
    const showVoidAction =
      status !== 'approved_internally' &&
      status !== 'payment_initialized' &&
      status !== 'paid';
    const voidAction = status === 'void' ? 'Reverse Void' : 'Void';

    return (
      <div
        data-testid="div-2dd250b7"
        className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6 lg:p-8"
      >
        {/* Header row */}
        <div className="flex flex-wrap items-start justify-between">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h2 data-testid="h2-bede8f08" className="my-0">
                {payout.number}
              </h2>
              <Badge
                variant="outline"
                data-testid="CargomaticBadge-4b4cde3d"
                className={`gap-1 ${BADGE_COLOR_MAP[payoutStatusColor] || ''}`}
              >
                {t(sluggify(payout.status))}
              </Badge>
            </div>
            {payout.due_date && (
              <span className="text-sm text-muted-foreground">
                {t('DUE_DATE')}: {moment(payout.due_date).format('L')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && (
              <PaymentMethodDialog
                open={this.state.showPaymentMethodDialog}
                onOpenChange={(open) => {
                  this.setState({
                    showPaymentMethodDialog: open,
                    ...(open ? { tempPaymentMethod: selectedValue } : {}),
                  });
                }}
                selectedValue={selectedValue}
                tempValue={this.state.tempPaymentMethod}
                onTempValueChange={(value) =>
                  this.setState({ tempPaymentMethod: value })
                }
                onSave={this.handleSavePaymentMethod}
                isSaving={this.state.isSavingPaymentMethod}
                disabled={!this.allowPaymentSelection()}
                label={this.getPaymentMethodLabel()}
                description={t('APPROVE_PAYOUTS_MSG_FOR_CARRIERS')}
              />
            )}
          </div>
        </div>

        {/* 3-column grid: carrier info + payments + audit */}
        <div className="w-full lg:grid lg:grid-cols-3 lg:gap-4 lg:items-start">
          {/* Carrier info card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <h3 className="my-0 text-base font-semibold">
                  {unescapeSpecialChars(payout.carrier?.name)}
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="button-d8ab3b86"
                  disabled={
                    payout.status !== 'internal_review' ||
                    this.state.isReprocessing
                  }
                  onClick={this.reprocessCarrierPayout}
                  className="gap-1"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  {t('REPROCESS_PAYOUT')}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <PaymentSummary
                total={payout.total}
                paid={payout.total_payments}
                balance={payout.amount_due}
                currency={payout.currency}
              />
              <div className="flex flex-wrap items-center gap-2">
                {showApprovalAction && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    onClick={() => this.setState({ showApprovalModal: true })}
                  >
                    <CheckCircle className="h-3.5 w-3.5" />
                    {t(sluggify(approvalAction))}
                  </Button>
                )}
                {showVoidAction && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1 text-red-600"
                    onClick={() => this.setState({ showVoidModal: true })}
                  >
                    <XCircle className="h-3.5 w-3.5" />
                    {t(sluggify(voidAction))}
                  </Button>
                )}
                <AuthorizationWrapper excludedRegions={[REGIONS.AP]}>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    onClick={() => this.setState({ showSendEmailModal: true })}
                  >
                    <Mail className="h-3.5 w-3.5" />
                    {titleize(t('EMAIL'))}
                  </Button>
                </AuthorizationWrapper>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1"
                  onClick={this.handleDownload}
                >
                  <Download className="h-3.5 w-3.5" />
                  {t('DOWNLOAD')}
                </Button>
                <a
                  data-testid="a-566c3eb2"
                  href={`/payouts/${payout._id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  {t('OPEN_IN_NEW_TAB')}
                </a>
              </div>
            </CardContent>
          </Card>

          {/* Payments card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <h3 className="my-0 text-base font-semibold">
                  {t('PAYMENTS')}
                </h3>
                {this.canMakePayment(payout) && (
                  <Button
                    size="sm"
                    className="bg-zinc-900 text-white hover:bg-zinc-800 gap-2"
                    data-testid="button-42a3d745"
                    onClick={() => this.setState({ showLogPaymentModal: true })}
                    disabled={this.state.balance === 0}
                  >
                    <DollarSign className="h-4 w-4" />
                    {t('RECORD_PAYMENT')}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <PaymentHistory
                payments={payout.payments ?? []}
                currency={payout.currency}
              />
            </CardContent>
          </Card>

          {/* Audit History card */}
          <Card className="max-h-96 flex flex-col overflow-hidden">
            <CardHeader>
              <h3 className="my-0 text-base font-semibold">
                {t('AUDIT_HISTORY')}
              </h3>
            </CardHeader>
            <CardContent className="overflow-y-auto">
              <HistoryTable data={payout.audit || []} type="Payout" />
            </CardContent>
          </Card>
        </div>

        {/* Jobs card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h3 className="my-0 text-base font-semibold">{t('JOBS')}</h3>
                <div className="w-60">
                  <ShipmentSearch
                    isFilter
                    lineItemGroups={lineItemGroups}
                    onSelect={(value, job) => {
                      this.handleSearch(payout, value, job);
                    }}
                    onChange={(value) => this.onSelectChange(value)}
                    disabled={!this.state.isFinanceAdmin}
                  />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <PayoutJobList payout={payout} />
          </CardContent>
        </Card>

        {/* Modals */}
        <SendEmailModal
          show={this.state.showSendEmailModal}
          onSend={this.sendEmail}
          onHide={() => this.setState({ showSendEmailModal: false })}
        />
        <ConfirmModal
          dataTestId="ConfirmModal-approval"
          show={this.state.showApprovalModal}
          title={t('VOID_MODAL_WITH_VALUE', {
            action: t(sluggify(approvalAction)),
            payoutID: payout.number,
          })}
          onConfirm={this.handleApprovalConfirm}
          onHide={() => this.setState({ showApprovalModal: false })}
          message={t('ARE_YOU_SURE')}
          hideButtonText={t('CANCEL')}
          affirmative={t('OK')}
        />
        <ConfirmModal
          dataTestId="ConfirmModal-void"
          show={this.state.showVoidModal}
          title={t('VOID_MODAL_WITH_VALUE', {
            action: t(sluggify(voidAction)),
            payoutID: payout.number,
          })}
          onConfirm={this.handleVoidConfirm}
          onHide={() => this.setState({ showVoidModal: false })}
          message={t('ARE_YOU_SURE')}
          hideButtonText={t('CANCEL')}
          affirmative={t('OK')}
          primaryButtonColor="red"
        />
        <ConfirmModal
          dataTestId="ConfirmModal-af080161"
          show={this.state.showLineItemConfirmModal}
          title={title}
          message={message}
          onConfirm={onConfirm}
          onHide={() => this.setState({ showLineItemConfirmModal: false })}
        />
        {this.state.showLogPaymentModal && (
          <LogPaymentModal
            show={this.state.showLogPaymentModal}
            amount={(this.state.balance || 0).toFixed(2)}
            onSave={this.savePayment}
            onHide={() => this.setState({ showLogPaymentModal: false })}
          />
        )}
      </div>
    );
  }

  renderPayoutV2() {
    const {
      payout,
      isAdmin,
      confirmModal: { title, message, onConfirm },
    } = this.state;
    const lineItemGroups = payout.lineItemGroups ?? [];
    const { t } = this.props;
    const selectedValue = this.defineDropdownSelectedValue();

    return (
      <div
        data-testid="div-2dd250b7"
        className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6 lg:p-8"
      >
        {/* Overview Card */}
        <PayoutHeader
          payout={payout}
          isModal={!!this.props.payoutId}
          onPayoutUpdated={this.handlePayoutUpdated}
        />

        {/* Tabbed Content Card */}
        <Card>
          <Tabs
            defaultValue="jobs"
            className="w-full"
            onValueChange={(value) => this.setState({ activeTab: value })}
          >
            <CardHeader className="pb-3">
              <TabsList className="justify-start">
                <TabsTrigger value="jobs">{t('LINE_ITEMS')}</TabsTrigger>
                <TabsTrigger value="payments">{t('PAYMENTS')}</TabsTrigger>
                <TabsTrigger value="audit">{t('AUDIT_HISTORY')}</TabsTrigger>
              </TabsList>
            </CardHeader>

            {/* Line Items Tab */}
            <TabsContent value="jobs" className="mt-0 space-y-4">
              <div className="px-6">
                <div className="w-full sm:w-80">
                  <ShipmentSearch
                    isFilter
                    lineItemGroups={lineItemGroups}
                    onSelect={(value, job) => {
                      this.handleSearch(payout, value, job);
                    }}
                    onChange={(value) => this.onSelectChange(value)}
                    disabled={!this.state.isFinanceAdmin}
                  />
                </div>
              </div>
              <CardContent className="overflow-x-auto">
                <PayoutJobList payout={payout} />
              </CardContent>
            </TabsContent>

            {/* Payments Tab */}
            <TabsContent value="payments" className="mt-0">
              <CardContent className="space-y-4">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <h3 className="text-base font-semibold my-0">
                    Payment History
                  </h3>
                  <div className="flex flex-wrap items-center gap-3">
                    {isAdmin && (
                      <PaymentMethodDialog
                        open={this.state.showPaymentMethodDialog}
                        onOpenChange={(open) => {
                          this.setState({
                            showPaymentMethodDialog: open,
                            ...(open
                              ? { tempPaymentMethod: selectedValue }
                              : {}),
                          });
                        }}
                        selectedValue={selectedValue}
                        tempValue={this.state.tempPaymentMethod}
                        onTempValueChange={(value) =>
                          this.setState({ tempPaymentMethod: value })
                        }
                        onSave={this.handleSavePaymentMethod}
                        isSaving={this.state.isSavingPaymentMethod}
                        disabled={!this.allowPaymentSelection()}
                        label={this.getPaymentMethodLabel()}
                        description={t('APPROVE_PAYOUTS_MSG_FOR_CARRIERS')}
                      />
                    )}
                    {this.canMakePayment(payout) && (
                      <Button
                        size="sm"
                        className="bg-zinc-900 text-white hover:bg-zinc-800 gap-2"
                        data-testid="button-42a3d745"
                        onClick={() =>
                          this.setState({ showLogPaymentModal: true })
                        }
                        disabled={this.state.balance === 0}
                      >
                        <DollarSign className="h-4 w-4" />
                        {t('RECORD_PAYMENT')}
                      </Button>
                    )}
                  </div>
                </div>
                <PaymentHistory
                  payments={payout.payments ?? []}
                  currency={payout.currency}
                />
              </CardContent>
            </TabsContent>

            {/* Audit History Tab */}
            <TabsContent value="audit" className="mt-0">
              <CardContent>
                <HistoryTable data={payout.audit || []} type="Payout" />
              </CardContent>
            </TabsContent>
          </Tabs>
        </Card>

        {/* Modals */}
        <ConfirmModal
          dataTestId="ConfirmModal-af080161"
          show={this.state.showLineItemConfirmModal}
          title={title}
          message={message}
          onConfirm={onConfirm}
          onHide={() => this.setState({ showLineItemConfirmModal: false })}
        />
        {this.state.showLogPaymentModal && (
          <LogPaymentModal
            show={this.state.showLogPaymentModal}
            amount={(this.state.balance || 0).toFixed(2)}
            onSave={this.savePayment}
            onHide={() => this.setState({ showLogPaymentModal: false })}
          />
        )}
      </div>
    );
  }

  renderPayout() {
    const order = this.state.order;
    if (isLegManagementEnabled(order)) {
      return this.renderPayoutV2();
    }
    return this.renderPayoutV1();
  }
}

PayoutDetails.propTypes = {
  match: PropTypes.object,
};

export default withTranslation()(PayoutDetails);
